import { useEffect, useState, type CSSProperties } from 'react';
import { linkProps } from '../router';
import {
  clubHref, countdown, dayText, hhmm, matchHref, resultClass, resultNl, statusLabel, tableHref,
  type FormItem, type SGroup, type SMatch, type SRow, type TeamSummary,
} from '../sportcenter';

/** Bouwstenen van het Sportcentrum, gedeeld door de pagina en de widgets. */

export function Crest({ logo, name, size = 28 }: { logo: string | null; name: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="sc-crest" style={{ width: size, height: size }} aria-hidden="true">
      {logo && !broken ? <img src={logo} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} /> : <span>{name.replace(/^(FC|AFC|SC|SV|AZ)\s/i, '').slice(0, 3).toUpperCase()}</span>}
    </span>
  );
}

export function StatusPill({ m }: { m: SMatch }) {
  const cls = m.cancelled ? 'sc-pill sc-pill-off' : m.state === 'in' ? 'sc-pill sc-pill-live' : m.state === 'post' ? 'sc-pill sc-pill-done' : 'sc-pill';
  return <span className={cls}>{m.state === 'in' && <i className="sc-dot" aria-hidden="true" />}{statusLabel(m)}</span>;
}

function goalsLine(m: SMatch, side: 'home' | 'away'): string {
  return m.incidents
    .filter((i) => i.side === side && i.kind !== 'red')
    .map((i) => `${i.player.split(' ').slice(-1)[0]} ${i.minute}${i.kind === 'pen' ? ' (p)' : i.kind === 'own' ? ' (e.d.)' : ''}`)
    .join(', ');
}

/** Eén wedstrijd als regel: tijd/minuut · thuis · stand · uit. */
export function MatchRow({ m, showLeague = false }: { m: SMatch; showLeague?: boolean }) {
  const score = m.state === 'pre' ? 'vs' : `${m.home.score ?? '-'} – ${m.away.score ?? '-'}`;
  const hg = goalsLine(m, 'home');
  const ag = goalsLine(m, 'away');
  return (
    <a className={`sc-row${m.state === 'in' ? ' is-live' : ''}${m.fav ? ' is-fav' : ''}`} {...linkProps(matchHref(m))}>
      <span className="sc-row-time"><StatusPill m={m} /></span>
      <span className="sc-row-team sc-home">
        <span className={`sc-name${m.home.winner ? ' is-win' : ''}`}>{m.home.short}{m.home.red ? <i className="sc-red" title={`${m.home.red}× rood`} /> : null}</span>
        <Crest logo={m.home.logo} name={m.home.short} />
      </span>
      <span className={`sc-row-score${m.state === 'pre' ? ' is-pre' : ''}`}>{score}</span>
      <span className="sc-row-team sc-away">
        <Crest logo={m.away.logo} name={m.away.short} />
        <span className={`sc-name${m.away.winner ? ' is-win' : ''}`}>{m.away.short}{m.away.red ? <i className="sc-red" title={`${m.away.red}× rood`} /> : null}</span>
      </span>
      {(hg || ag || showLeague) && (
        <span className="sc-row-goals subtle">
          <span className="sc-home">{hg}</span>
          <span className="sc-row-mid">{showLeague ? m.leagueName : ''}</span>
          <span className="sc-away">{ag}</span>
        </span>
      )}
    </a>
  );
}

export function FormChips({ form, big = false }: { form: FormItem[]; big?: boolean }) {
  if (!form.length) return <span className="subtle">Nog geen uitslagen</span>;
  return (
    <span className={`sc-form${big ? ' sc-form-big' : ''}`}>
      {form.map((f) => (
        <a key={f.id} className="sc-form-item" {...linkProps(matchHref(f))} title={`${f.home ? 'thuis' : 'uit'} tegen ${f.opp} · ${f.score} · ${f.league}`}>
          <span className={`sc-res ${resultClass(f.r)}`}>{resultNl(f.r)}</span>
          {big && <Crest logo={f.oppLogo} name={f.opp} size={22} />}
          {big && <span className="sc-form-opp">{f.opp}</span>}
        </a>
      ))}
    </span>
  );
}

/** Aftelring naar de aftrap (vult zich in de laatste week). */
export function CountdownRing({ m, size = 128 }: { m: SMatch; size?: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const left = Math.max(0, m.start - now);
  const frac = 1 - Math.min(1, left / (7 * 86_400_000));
  const r = 44;
  const c = 2 * Math.PI * r;
  return (
    <span className="sc-ring" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r={r} className="sc-ring-bg" />
        <circle cx="50" cy="50" r={r} className="sc-ring-fg" strokeDasharray={`${c * frac} ${c}`} transform="rotate(-90 50 50)" />
      </svg>
      <span className="sc-ring-text"><small>Begint over</small><strong>{countdown(m.start, now)}</strong></span>
    </span>
  );
}

/** Scorebord van één wedstrijd: groot (wedstrijdpagina, clubkaart). */
export function ScoreBoard({ m, compact = false }: { m: SMatch; compact?: boolean }) {
  return (
    <div className={`sc-board${compact ? ' sc-board-compact' : ''}`}>
      <a className="sc-board-team" {...linkProps(clubHref({ path: m.path, id: m.home.id }))}>
        <Crest logo={m.home.logo} name={m.home.short} size={compact ? 44 : 72} />
        <span>{m.home.short}</span>
      </a>
      <div className="sc-board-mid">
        {m.state === 'pre' ? (
          <span className="sc-board-time">{hhmm(m.start)}</span>
        ) : (
          <span className="sc-board-score"><b className={m.away.winner ? 'is-lose' : ''}>{m.home.score ?? '-'}</b><i>:</i><b className={m.home.winner ? 'is-lose' : ''}>{m.away.score ?? '-'}</b></span>
        )}
        <StatusPill m={m} />
        {m.state === 'pre' && <span className="subtle sc-board-day">{dayText(m.start)}</span>}
      </div>
      <a className="sc-board-team" {...linkProps(clubHref({ path: m.path, id: m.away.id }))}>
        <Crest logo={m.away.logo} name={m.away.short} size={compact ? 44 : 72} />
        <span>{m.away.short}</span>
      </a>
    </div>
  );
}

/** "Je club in beeld": volgende wedstrijd of live stand, positie, vorm en cijfers. */
export function ClubCard({ s, compact = false }: { s: TeamSummary; compact?: boolean }) {
  const t = s.team;
  const m = s.live ?? s.next;
  const st = s.standing;
  const played = st?.played ?? s.form.length;
  const winPct = st && st.played ? Math.round((st.w / st.played) * 100) : null;
  const tint = t.color ? `#${t.color}` : undefined;
  return (
    <article className={`sc-club${compact ? ' sc-club-compact' : ''}`} style={tint ? ({ '--sc-tint': tint } as CSSProperties) : undefined}>
      <a className="sc-club-head" {...linkProps(clubHref(t))}>
        <Crest logo={t.logo} name={t.short} size={compact ? 36 : 48} />
        <span className="sc-club-name"><strong>{t.name}</strong><small>{st ? `#${st.rank} van ${st.of} · ${t.leagueName}` : t.leagueName}</small></span>
      </a>
      {m ? (
        <div className="sc-club-match">
          <span className="eyebrow">{s.live ? 'Nu live' : 'Volgende wedstrijd'} · {m.leagueName}</span>
          {s.live || compact ? <a className="sc-club-board" {...linkProps(matchHref(m))}><ScoreBoard m={m} compact /></a> : (
            <a className="sc-club-next" {...linkProps(matchHref(m))}>
              <span className="sc-board-team"><Crest logo={m.home.logo} name={m.home.short} size={56} /><span>{m.home.short}</span></span>
              <CountdownRing m={m} size={116} />
              <span className="sc-board-team"><Crest logo={m.away.logo} name={m.away.short} size={56} /><span>{m.away.short}</span></span>
            </a>
          )}
          {!s.live && <span className="sc-club-when"><b>{dayText(m.start)}</b><span>{hhmm(m.start)}</span>{m.home.id === t.id ? <em>Thuis</em> : <em>Uit</em>}</span>}
        </div>
      ) : <p className="subtle">Geen wedstrijden gepland.</p>}
      <div className="sc-club-form"><span className="sc-label">Vorm</span><FormChips form={s.form} /></div>
      {!compact && (
        <div className="sc-tiles">
          <span className="sc-tile"><b>{st ? `#${st.rank}` : '–'}</b><small>Positie{st ? ` van ${st.of}` : ''}</small></span>
          <span className="sc-tile"><b>{st?.points ?? '–'}</b><small>Punten</small></span>
          <span className="sc-tile"><b>{winPct != null ? `${winPct}%` : '–'}</b><small>Winst</small></span>
          <span className="sc-tile"><b>{played}</b><small>Gespeeld</small></span>
          {st && (
            <span className="sc-tile sc-tile-wide">
              <span className="sc-wdl" aria-hidden="true">
                <i className="sc-w" style={{ flex: st.w || 0.001 }} /><i className="sc-d" style={{ flex: st.d || 0.001 }} /><i className="sc-l" style={{ flex: st.l || 0.001 }} />
              </span>
              <small>{st.w} W · {st.d} G · {st.l} V · doelsaldo {st.gd || `${st.gf - st.ga}`} ({st.gf}–{st.ga})</small>
            </span>
          )}
        </div>
      )}
    </article>
  );
}

/** Stand; met `around` alleen de rijen rond die club. */
export function StandingsTable({ groups, highlight, around, league, path, compact = false }: { groups: SGroup[]; highlight: Set<string>; around?: string; league?: string; path: string; compact?: boolean }) {
  const us = groups.some((g) => g.rows.some((r) => r.pct != null));
  const pick = (rows: SRow[]) => {
    if (!around) return rows;
    const i = rows.findIndex((r) => r.team.id === around);
    if (i < 0) return rows.slice(0, 5);
    const from = Math.max(0, Math.min(rows.length - 5, i - 2));
    return rows.slice(from, from + 5);
  };
  const list = around ? groups.filter((g) => g.rows.some((r) => r.team.id === around)).slice(0, 1) : groups;
  return (
    <div className={`sc-table-wrap${compact ? ' sc-table-compact' : ''}`}>
      {(list.length ? list : groups.slice(0, 1)).map((g) => (
        <table key={g.name} className="sc-table">
          {groups.length > 1 && !compact && <caption>{g.name}</caption>}
          <thead>
            <tr>
              <th>#</th><th className="sc-t-team">Club</th><th title="Gespeeld">G</th>
              {!compact && <><th>W</th><th>{us ? 'V' : 'G'}</th>{!us && <th>V</th>}</>}
              {!compact && !us && <th title="Doelsaldo">DS</th>}
              <th>{us ? '%' : 'P'}</th>
            </tr>
          </thead>
          <tbody>
            {pick(g.rows).map((r) => (
              <tr key={r.team.id} className={highlight.has(r.team.id) ? 'is-fav' : undefined}>
                <td className="sc-t-rank" style={r.note ? ({ '--note': r.note.color } as CSSProperties) : undefined} title={r.note?.text}>{r.rank}</td>
                <td className="sc-t-team"><a {...linkProps(clubHref({ path, id: r.team.id }))}><Crest logo={r.team.logo} name={r.team.short} size={20} /><span>{r.team.short}</span></a></td>
                <td>{r.played}</td>
                {!compact && <><td>{r.w}</td>{!us && <td>{r.d}</td>}<td>{r.l}</td></>}
                {!compact && !us && <td>{r.gd}</td>}
                <td className="sc-t-pts">{us ? r.pct : r.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
      {compact && league && <a className="sc-more" {...linkProps(tableHref(league))}>Hele stand ›</a>}
    </div>
  );
}

/** Live-ticker: doorschuivende band met standen. */
export function Ticker({ matches, title }: { matches: SMatch[]; title: string }) {
  const items = matches.map((m) => (
    <a key={m.id} className={`sc-tick${m.state === 'in' ? ' is-live' : ''}${m.fav ? ' is-fav' : ''}`} {...linkProps(matchHref(m))}>
      <Crest logo={m.home.logo} name={m.home.short} size={22} />
      <span className="sc-tick-abbr">{m.home.abbr || m.home.short}</span>
      <b className="sc-tick-score">{m.state === 'pre' ? hhmm(m.start) : `${m.home.score ?? '-'}-${m.away.score ?? '-'}`}</b>
      <span className="sc-tick-abbr">{m.away.abbr || m.away.short}</span>
      <Crest logo={m.away.logo} name={m.away.short} size={22} />
      {m.state !== 'pre' && <small className="sc-tick-st">{statusLabel(m)}</small>}
    </a>
  ));
  const scroll = matches.length > 4;
  return (
    <section className="row sc-ticker-row" aria-label={title}>
      <h2 className="row-title-link"><a {...linkProps('/sportcenter')}>{title} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
      <div className={`sc-ticker${scroll ? ' is-scrolling' : ''}`} data-focus-group>
        <div className="sc-ticker-track" style={scroll ? ({ '--sc-dur': `${Math.max(20, matches.length * 5)}s` } as CSSProperties) : undefined}>
          {items}
          {scroll && <span className="sc-ticker-dup" aria-hidden="true">{matches.map((m) => <span key={`d${m.id}`} className={`sc-tick${m.state === 'in' ? ' is-live' : ''}`}><Crest logo={m.home.logo} name={m.home.short} size={22} /><span className="sc-tick-abbr">{m.home.abbr || m.home.short}</span><b className="sc-tick-score">{m.state === 'pre' ? hhmm(m.start) : `${m.home.score ?? '-'}-${m.away.score ?? '-'}`}</b><span className="sc-tick-abbr">{m.away.abbr || m.away.short}</span><Crest logo={m.away.logo} name={m.away.short} size={22} />{m.state !== 'pre' && <small className="sc-tick-st">{statusLabel(m)}</small>}</span>)}</span>}
        </div>
      </div>
    </section>
  );
}
